import { Box, Button, Typography, IconButton } from '@mui/material';
import { dismissJoinGroups } from '../../api/userApi';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TelegramIcon from '@mui/icons-material/Telegram';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';
import { useUser } from '../../context/userContext';

// The sales community groups every agent should be in. One place for every link —
// they appear on the Tutorials page, in the first-login banner and on the dashboard.
// (user 2026-09-25)
//
// WhatsApp is now TWO groups, one per language, so an agent joins the one they can
// actually read rather than a single room running in both. Telegram stays one group,
// EN/BM. (user 2026-10-06: "i wanna put (CN) and (EN/BM) two group in whatsapp grp" /
// "telegram (EN/BM)")
export const SALES_WHATSAPP_EN_URL = 'https://chat.whatsapp.com/EAPld5vcwvwJrDs1gc36ue';
export const SALES_WHATSAPP_CN_URL = 'https://chat.whatsapp.com/IKG6wW4sOM87Iwo7FF2SYH';
export const SALES_TELEGRAM_URL = 'https://t.me/+6-Qin0UCtnljNjhl';

// Drawn in this order everywhere, so the row reads the same on every page.
const GROUPS = [
    { key: 'wa-en', label: 'WhatsApp (EN/BM)', url: SALES_WHATSAPP_EN_URL, kind: 'whatsapp' },
    { key: 'wa-cn', label: 'WhatsApp (CN)', url: SALES_WHATSAPP_CN_URL, kind: 'whatsapp' },
    { key: 'tg', label: 'Telegram (EN/BM)', url: SALES_TELEGRAM_URL, kind: 'telegram' },
];

const PP = "'Poppins', sans-serif";

/**
 * Has this person put the group prompts away?
 *
 * The X on the banner now settles THE BANNER, and only the banner. It used to hide the
 * dashboard tiles and the Tutorials card as well, which left somebody who had dismissed
 * the greeting with no way to reach the groups at all — the links had to be dug back out
 * of localStorage. (user 2026-10-05: "banner is banner. when banner turn off then its ok";
 * the earlier all-or-nothing behaviour came from 2026-10-03: "still the banner not going
 * away after i x", which was about the banner coming BACK, not about the tiles.)
 *
 * The account flag is the truth and arrives with MyRights on every boot. localStorage
 * is only an instant local guard, so nothing flashes back before MyRights lands and a
 * dismissal still sticks if the POST fails offline.
 */
export function useJoinGroupsDismissed() {
    const { user } = useUser();
    if (user?.joinGroupsDismissed) return true;
    if (!user?.userId) return false;
    try { return localStorage.getItem(`JoinGroupsSeen:${user.userId}`) === 'true'; } catch { return false; }
}

/** The two join buttons. `size="small"` for tight spots like the dashboard tiles. */
export function JoinGroupButtons({ size = 'medium', fullWidth = false, alwaysShow = false }) {
    // Gone once the banner's X has been clicked, unless a caller insists — the X is
    // meant to settle the group prompts everywhere, not just at the top of the page.
    const dismissed = useJoinGroupsDismissed();
    // On a phone the two buttons share the row and shrink their label rather than colliding;
    // below ~300px they wrap onto separate lines. (user 2026-09-25 — mobile check)
    const sx = {
        fontFamily: PP, textTransform: 'none', fontWeight: 600, borderRadius: '10px',
        boxShadow: 'none', px: { xs: 1.25, md: 2 },
        fontSize: { xs: '0.76rem', md: '0.875rem' },
        '& .MuiButton-startIcon': { mr: { xs: 0.5, md: 1 } },
        minWidth: 0,
        ...(fullWidth ? { flex: '1 1 118px' } : {}),
    };
    if (dismissed && !alwaysShow) return null;
    return (
        // `minWidth: 0` and the full-width basis on a phone are what stop the third button
        // running off the edge. A flex item defaults to min-width:auto — it refuses to shrink
        // below its own content — so this row grew past the screen and the wrap inside it never
        // triggered: two buttons showed and Telegram sat off the right of the dashboard.
        // (user 2026-10-06: "Its blocking")
        <Box sx={{
            display: 'flex', gap: 1, flexWrap: 'wrap', minWidth: 0,
            flexBasis: { xs: '100%', md: 'auto' },
            flexGrow: fullWidth ? 1 : 0, flexShrink: 1,
        }}>
            {GROUPS.map(g => (
                <Button
                    key={g.key}
                    variant="contained" size={size} href={g.url} target="_blank" rel="noopener noreferrer"
                    startIcon={g.kind === 'whatsapp' ? <WhatsAppIcon /> : <TelegramIcon />}
                    sx={{
                        ...sx,
                        ...(g.kind === 'whatsapp'
                            ? { bgcolor: '#25D366', '&:hover': { bgcolor: '#1DA851' } }
                            : { bgcolor: '#229ED9', '&:hover': { bgcolor: '#1B7FAE' } }),
                    }}
                >
                    {g.label}
                </Button>
            ))}
        </Box>
    );
}

/**
 * The card that sits above the progress bar on Tutorials.
 *
 * It STAYS after the welcome banner is dismissed. The X belongs to the banner and
 * nothing else — a person who has silenced the greeting still needs a way to reach the
 * groups. (user 2026-10-05: "banner is banner. when banner turn off then its ok")
 */
export function JoinGroupsCard({ title = 'Join our sales community', subtitle = 'Announcements, pricing updates and day-to-day help from the team — join the WhatsApp group in your language, and Telegram too.' }) {
    return (
        <Box sx={{
            bgcolor: '#fff', borderRadius: '14px', p: { xs: 1.75, md: 2.25 }, mb: 2,
            border: '1px solid #E2E8F0', display: 'flex', gap: 2,
            alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between',
            flexDirection: { xs: 'column', md: 'row' },
        }}>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: '0.95rem', color: '#0F172A' }}>{title}</Typography>
                <Typography sx={{ fontFamily: PP, fontSize: '0.8rem', color: '#64748B', mt: 0.3 }}>{subtitle}</Typography>
            </Box>
            <JoinGroupButtons alwaysShow />
        </Box>
    );
}

/**
 * First-login banner — shown once per user, then never again on this device.
 * Dismissal is per user id so a shared laptop doesn't hide it from the next person.
 */
export function JoinGroupsBanner() {
    const { user, setUser } = useUser();
    const key = user?.userId ? `JoinGroupsSeen:${user.userId}` : null;
    // Dismissal lives on the ACCOUNT (user.JoinGroupsDismissedOn, delivered with
    // MyRights on every boot). localStorage is kept only as an instant local guard
    // so the banner cannot flash back before MyRights lands, and so a dismissal
    // still sticks if the POST fails offline. On its own it was the bug: it is
    // scoped to one origin in one browser, so dismissing on the laptop left it
    // showing on the phone, and local and prod each had their own copy — which
    // read as "the X does nothing". (user 2026-10-02)
    const [localDismissed, setLocalDismissed] = useState(false);
    const alreadyDismissed = useJoinGroupsDismissed();
    if (!key || localDismissed || alreadyDismissed) return null;
    const close = () => {
        try { localStorage.setItem(key, 'true'); } catch { /* private mode */ }
        setLocalDismissed(true);
        // Remember it for the account; the banner is already gone either way.
        dismissJoinGroups().catch(() => { /* offline — localStorage still holds it here */ });
        setUser?.(prev => (prev ? { ...prev, joinGroupsDismissed: true } : prev));
    };
    return (
        // The close button is part of the row, never floating over it — as an absolute X it
        // sat on top of the Telegram button on a narrow screen. (user 2026-09-25)
        <Box sx={{
            mb: 2, p: { xs: 1.5, md: 2 }, borderRadius: '14px',
            background: 'linear-gradient(135deg,#0F172A,#1E3A8A)', color: '#fff',
            display: 'flex', gap: { xs: 1.25, md: 2 }, alignItems: 'center', flexWrap: 'wrap',
        }}>
            <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
                <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: { xs: '0.92rem', md: '1rem' } }}>Welcome aboard — join the sales groups</Typography>
                <Typography sx={{ fontFamily: PP, fontSize: { xs: '0.76rem', md: '0.82rem' }, color: 'rgba(255,255,255,0.8)', mt: 0.3 }}>
                    Pricing changes, promos and answers to your questions land there first.
                </Typography>
            </Box>
            {/* On a phone the buttons take the full row and the X sits beside them, so a thumb
                can reach all three without anything overlapping. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: { xs: '1 1 100%', md: '0 0 auto' } }}>
                <JoinGroupButtons fullWidth />
                <IconButton onClick={close} size="small" aria-label="Dismiss"
                    sx={{ color: 'rgba(255,255,255,0.75)', flex: '0 0 auto', border: '1px solid rgba(255,255,255,0.25)' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
        </Box>
    );
}
